import React, { useState, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { g as activityCatalog } from '../../activity_catalog';

/**
 * Componente reutilizable para seleccionar código de actividad económica
 * con autocomplete filtrable por código o nombre
 * 
 * @param {string|object} value - Valor seleccionado (puede ser código string o objeto {code, name})
 * @param {function} onChange - Función callback cuando cambia la selección (retorna el código)
 * @param {function} onSelect - Función callback opcional cuando se selecciona (retorna el objeto completo {code, name})
 * @param {string} label - Etiqueta del campo
 * @param {boolean} required - Si es requerido (opcional)
 * @param {string} error - Mensaje de error (opcional)
 * @param {boolean} disabled - Si está deshabilitado (opcional)
 */
const EconomicActivitySelect = ({ 
  value, 
  onChange,
  onSelect,
  label = 'Código de Actividad Económica',
  required = false,
  error = '',
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  
  // Cargar el catálogo una sola vez con useMemo
  const activities = useMemo(() => {
    const catalog = activityCatalog();
    return catalog.map(item => ({
      code: item.code,
      name: `${item.code} - ${item.name}`
    }));
  }, []);

  // Determinar si está cargando (para futuro uso con datos async)
  const loading = false;

  // Encontrar el valor actual basado en el código
  const currentValue = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object' && value !== null) {
      return value;
    }
    return activities.find(a => a.code === value) || null;
  }, [value, activities]);

  // Manejar cambio de selección
  const handleChange = (event, newValue) => {
    onChange(newValue ? newValue.code : '');
    // También llamar onSelect si existe para obtener el nombre completo
    if (onSelect && newValue) {
      onSelect(newValue);
    }
  };

  // Filtrar opciones mientras escribe
  const filterOptions = (options, state) => {
    const inputValue = state.inputValue.toLowerCase();
    return options.filter(option => 
      option.code.toLowerCase().includes(inputValue) ||
      option.name.toLowerCase().includes(inputValue)
    );
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      loading={loading}
      value={currentValue}
      onChange={handleChange}
      filterOptions={filterOptions}
      options={activities}
      disabled={disabled}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, val) => option.code === (val?.code || val)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        // Separar código y nombre para mejor visualización
        const [code, ...nameParts] = option.name.split(' - ');
        const fullName = nameParts.join(' - ');
        return (
          <li key={key} {...rest}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 600 }}>{code}</span>
              <span style={{ fontSize: '0.85em', color: '#666' }}>{fullName}</span>
            </div>
          </li>
        );
      }}
      noOptionsText="No se encontraron actividades"
      loadingText="Cargando..."
      placeholder="Seleccione una actividad"
    />
  );
};

export default EconomicActivitySelect;